import { LLM_CONFIG, ModelConfig } from '../../config';
import { SceneData, LLMResponse } from '../../types';
import { BaseLLMAdapter } from './BaseLLMAdapter';
import { ResponseProcessor } from '../../processors/response';
import { ReasoningVisualizer } from '../../components/ReasoningVisualizer';

/**
 * DeepSeek LLM适配器实现，处理DeepSeek相关的特殊逻辑
 */
export class DeepSeekAdapter extends BaseLLMAdapter {
  private responseProcessor: ResponseProcessor;
  private reasoningVisualizer: ReasoningVisualizer;
  
  /**
   * 构造函数
   * @param config 模型配置
   * @param debugMode 是否处于调试模式
   */
  constructor(config: ModelConfig, debugMode: boolean = false) {
    super(config, debugMode);
    this.responseProcessor = new ResponseProcessor(debugMode);
    this.reasoningVisualizer = ReasoningVisualizer.getInstance();
  }
  
  /**
   * 准备DeepSeek请求
   */
  async prepareRequest(prompt: string): Promise<{
    endpoint: string;
    headers: Headers;
    body: any;
  }> {
    const endpoint = this.getEndpoint();
    const headers = this.createBaseHeaders();
    
    // 构建请求体
    const body = this.createBaseBody(prompt);
    
    // 根据模型ID进行特殊处理
    if (this.config.id === 'Pro/deepseek-ai/DeepSeek-V3') {
      // siliconflow平台的DeepSeek-V3
      body.max_tokens = Math.min(LLM_CONFIG.maxTokens, 4000); // 确保不超过4096
      body.temperature = LLM_CONFIG.temperature;
      body.top_p = LLM_CONFIG.topP;
      body.top_k = LLM_CONFIG.topK;
      body.frequency_penalty = LLM_CONFIG.frequencyPenalty;
      body.n = LLM_CONFIG.n;
      body.response_format = { type: "text" };
      
      // 显示推理可视化
      console.log('显示DeepSeekV3推理可视化');
      this.reasoningVisualizer.show(this.config.id, this.config.vendor || 'siliconflow');
    } else if (this.config.id === 'deepseek-reasoner') {
      // DeepSeek-R1 推理模型
      body.model = 'deepseek-reasoner';
      body.reasoning = true;
      body.max_tokens = LLM_CONFIG.maxTokens;
      body.temperature = LLM_CONFIG.temperature;
      
      // 对于推理模型，显示推理可视化
      console.log('显示DeepSeek-R1推理可视化');
      this.reasoningVisualizer.show(this.config.id, this.config.vendor || 'deepseek');
    } else {
      body.model = 'deepseek-chat';
      
      // 为所有DeepSeek模型显示可视化
      console.log('显示普通DeepSeek推理可视化');
      this.reasoningVisualizer.show(this.config.id, this.config.vendor || 'deepseek');
    }
    
    // 添加系统消息
    body.messages.unshift(this.createSystemMessage(
      "你是一个赛博朋克2077风格游戏世界的创造者和叙事者。这是一个充满高科技和低生活的世界，霓虹与阴影并存，义体与人性交织。" + 
      (this.config.id === 'deepseek-reasoner' ? "请在回答时展示你的推理过程。" : "")
    ));
    
    // 设置流式输出
    body.stream = true;
    
    this.logDebug('DeepSeek请求详情:', {
      endpoint,
      model: this.config.id,
      headers: Array.from(headers.entries()),
      body
    });
    
    return { endpoint, headers, body };
  }
  
  /**
   * 处理DeepSeek响应
   */
  async processResponse(response: Response): Promise<SceneData | null> {
    // 处理流式响应
    return this.handleStreamResponse(response);
  }
  
  /**
   * 处理流式响应
   */
  private async handleStreamResponse(response: Response): Promise<SceneData | null> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    let accumulatedContent = '';
    let accumulatedReasoning = '';
    let isFirstReasoningChunk = true;
    
    this.logDebug(`开始处理流式响应: 模型=${this.config.id}`, {});
    
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        // 将Uint8Array转换为字符串
        const chunk = new TextDecoder().decode(value);
        
        this.logDebug('流式响应数据片段:', chunk);
        
        // 处理SSE格式的数据
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ') && !line.includes('[DONE]')) {
            try {
              const jsonStr = line.substring(6); // 去掉 "data: "
              const jsonData = JSON.parse(jsonStr);
              
              this.logDebug('解析后的JSON数据:', jsonData);
              
              // DeepSeek-R1 特殊处理推理内容
              if (this.config.id === 'deepseek-reasoner') {
                this.logDebug('DeepSeek-R1 响应片段:', {
                  model: this.config.id,
                  data: jsonData,
                  hasReasoning: jsonData.choices?.[0]?.delta?.reasoning_content !== undefined,
                  hasContent: jsonData.choices?.[0]?.delta?.content !== undefined
                });
                
                // 处理推理内容
                if (jsonData.choices?.[0]?.delta?.reasoning_content !== undefined) {
                  const reasoningContent = jsonData.choices[0].delta.reasoning_content;
                  
                  // 对第一个推理块特殊处理
                  if (isFirstReasoningChunk) {
                    accumulatedReasoning = reasoningContent;
                    isFirstReasoningChunk = false;
                  } else {
                    accumulatedReasoning += reasoningContent;
                  }
                  
                  // 更新推理可视化
                  this.reasoningVisualizer.update(accumulatedReasoning);
                }
                
                // 处理实际内容
                if (jsonData.choices?.[0]?.delta?.content !== undefined) {
                  accumulatedContent += jsonData.choices[0].delta.content;
                }
              } else {
                // 普通DeepSeek模型处理
                if (jsonData.choices?.[0]?.delta?.content !== undefined) {
                  const content = jsonData.choices[0].delta.content;
                  accumulatedContent += content;
                  
                  // 更新推理可视化（对于所有模型）
                  this.reasoningVisualizer.update(accumulatedContent);
                }
              }
            } catch (error) {
              console.error('解析SSE数据时出错:', error);
            }
          }
        }
      }
      
      this.logDebug('累积内容:', accumulatedContent);
      
      // 完成时隐藏推理可视化
      setTimeout(() => {
        this.reasoningVisualizer.hide();
      }, 1000);
      
      // 将累积的内容处理为SceneData
      const mockResponse: LLMResponse = {
        choices: [
          {
            message: {
              content: accumulatedContent
            }
          }
        ]
      };
      
      return this.responseProcessor.processResponse(mockResponse);
    } catch (error) {
      // 发生错误时隐藏推理可视化
      this.reasoningVisualizer.hide();
      
      console.error('处理流式响应时出错:', error);
      throw error;
    }
  }
} 